import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Check, Circle, Lock } from 'lucide-react';
import { Button } from '@dru/ui';
import { api } from '../lib/api';
import { errorMessage, useAuth } from '../lib/auth';
import { landingFor } from '../layouts/ConsoleShell';
import { AuthFrame } from './AuthFrame';
import { PASSWORD_MIN_LENGTH, PASSWORD_RULES } from '../lib/password-policy';
import { CONTROL, ErrorNote, Field, GRADIENT_BTN, LINK_BTN, QUIET_LINK } from './Login';


/**
 * Where the emailed link lands: `/invitation/:token` for a first password,
 * `/reset-password/:token` for a replacement. One screen, because the act is
 * the same — choose a password for the account this link names — and the API
 * redeems both through the same route. Only the words differ.
 *
 * The token travels in the request BODY, never as a query string on an API
 * call, so it does not reach an access log a second time. It is in this
 * page's URL because the email put it there; that is the one copy that has
 * to exist.
 *
 * Asked first whether the link still works, so an expired one says so before
 * anyone types a password twice. A working link also names the address it is
 * for, so somebody with two accounts knows which one they are setting.
 *
 * Once the password is saved the person is signed in with it, through the
 * same `/auth/login` the sign-in form uses, and lands in their console. Typing
 * the address and the password again a second later proved nothing. An
 * account with a second factor still has to give its code, so that case — and
 * any failure — falls back to the sign-in page.
 */
export function SetPassword({ purpose }: { purpose: 'invitation' | 'reset' }) {
  const { token = '' } = useParams<{ token: string }>();
  const nav = useNavigate();
  const { login } = useAuth();
  const [state, setState] = useState<'checking' | 'ready' | 'dead' | 'unavailable' | 'done'>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [mfaRecommended, setMfaRecommended] = useState(false);
  const [email, setEmail] = useState('');
  // Signed in after the save, but held on the "saved" screen for a sentence
  // about two-factor authentication; the button then lands them.
  const [landing, setLanding] = useState<string | null>(null);

  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setState('checking');
    api.post<{ redeemable: boolean; email?: string }>('/auth/invitation/state', { token })
      .then((r) => {
        if (cancelled) return;
        if (r.email) setEmail(r.email);
        setState(r.redeemable ? 'ready' : 'dead');
      })
      .catch(() => { if (!cancelled) setState('unavailable'); });
    return () => { cancelled = true; };
  }, [token, retry]);

  // The same rules the server applies, ticked off as they are met. Nothing is
  // ticked on an empty field: "not easily guessed" is true of nothing typed.
  const checks = PASSWORD_RULES.map((r) => ({ ...r, ok: password.length > 0 && r.test(password) }));
  const strong = checks.every((c) => c.ok);
  const weak = password.length > 0 && !strong;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = strong && confirm === password;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready) return;
    setBusy(true); setError(null);
    try {
      const r = await api.post<{ redeemed: true; email: string; mfaRecommended: boolean }>(
        '/auth/invitation/redeem', { token, password },
      );
      setMfaRecommended(r.mfaRecommended);
      setEmail(r.email);
      // The password is saved whatever happens next; signing in is a courtesy
      // on top, and anything short of a session leaves "Go to sign in".
      try {
        const outcome = await login(r.email, password);
        if (outcome.kind === 'session' && outcome.principal) {
          const to = landingFor(new Set(outcome.principal.permissions), outcome.principal);
          if (!r.mfaRecommended) { nav(to, { replace: true }); return; }
          setLanding(to);
        }
      } catch {
        // Fall through to the sign-in link.
      }
      setState('done');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const first = purpose === 'invitation';
  const title = first ? 'Set your password' : 'Choose a new password';
  const lead = first
    ? 'Your account is ready. Choose the password you will sign in with — nobody else has it.'
    : 'Choose a replacement. Your old password stops working as soon as this one is saved.';

  if (state === 'checking') {
    return (
      <AuthFrame title={title} lead={lead}>
        <p role="status" className="text-small text-neutral-500">Checking your link…</p>
      </AuthFrame>
    );
  }

  if (state === 'unavailable') {
    return <AuthFrame title="We could not check this link" lead="The service could not be reached. This does not mean your link has expired. Please try again.">
      <Button className="w-full" onClick={() => setRetry(value => value + 1)}>Try again</Button>
      <p className="mt-4 text-center"><Link to="/login" className={QUIET_LINK}>Back to sign in</Link></p>
    </AuthFrame>;
  }

  if (state === 'dead') {
    return (
      <AuthFrame
        title="This link no longer works"
        lead={first
          ? 'Invitation links are good for 72 hours and work once. Ask your administrator to send a new one.'
          : 'Reset links are good for one hour and work once. Ask for another and use it straight away.'}
      >
        {!first && (
          <Link to="/forgot-password" className={LINK_BTN}>Request a new link</Link>
        )}
        <p className="mt-4 text-center">
          <Link to="/login" className={QUIET_LINK}>
            Back to sign in
          </Link>
        </p>
      </AuthFrame>
    );
  }

  if (state === 'done') {
    return (
      <AuthFrame
        title="Password saved"
        lead={landing
          ? 'You are signed in.'
          : 'Sign in with your work email and the password you just chose.'}
      >
        {mfaRecommended && (
          <p className="mb-4 rounded-input border border-warning-600/30 bg-warning-100 px-3 py-2.5 text-caption font-normal text-neutral-800">
            Your role includes actions a password alone should not be enough for. Once you are
            in, turn on two-factor authentication under Security.
          </p>
        )}
        {landing
          ? <Link to={landing} replace className={LINK_BTN}>Continue</Link>
          // The email rides along to the sign-in form, so the browser's
          // password manager offers to save this login on the first sign-in.
          : <Link to="/login" state={{ email }} className={LINK_BTN}>Go to sign in</Link>}
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={title} lead={lead}>
      <form onSubmit={submit} noValidate>
        {email && (
          <p className="mb-4 text-small text-neutral-600">
            For <span className="font-medium text-neutral-900 break-all">{email}</span>
          </p>
        )}
        {/* For the password manager: it saves the login under this address. */}
        {email && <input type="email" name="username" autoComplete="username" value={email} readOnly hidden />}
        <Field label="New password" icon={<Lock className="h-4 w-4" />}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            autoFocus
            minLength={PASSWORD_MIN_LENGTH}
            aria-invalid={weak || undefined}
            aria-describedby="new-password-rules"
            className={CONTROL}
          />
        </Field>
        <ul id="new-password-rules" aria-label="Password requirements" className="mt-2 space-y-1">
          {checks.map((c) => (
            <li key={c.key} className={`flex items-center gap-2 text-caption ${c.ok ? 'text-covered-600' : 'text-neutral-500'}`}>
              {c.ok
                ? <Check aria-hidden className="h-3.5 w-3.5 shrink-0" />
                : <Circle aria-hidden className="h-3 w-3 shrink-0" />}
              <span>{c.label}</span>
              <span className="sr-only">{c.ok ? ' — met' : ' — not yet'}</span>
            </li>
          ))}
        </ul>

        <div className="mt-4">
          <Field label="Type it again" icon={<Lock className="h-4 w-4" />}>
            <input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              aria-invalid={mismatch || undefined}
              aria-describedby={mismatch ? 'confirm-password-mismatch' : undefined}
              className={CONTROL}
            />
          </Field>
          {mismatch && (
            <p id="confirm-password-mismatch" className="mt-1.5 text-caption font-normal text-danger-600">The two do not match.</p>
          )}
        </div>

        {error && <ErrorNote>{error}</ErrorNote>}

        <Button type="submit" loading={busy} disabled={!ready} className={`mt-6 w-full ${GRADIENT_BTN}`}>
          {first ? 'Set password and finish' : 'Save new password'}
        </Button>
      </form>
    </AuthFrame>
  );
}
